import { useState } from "react";
import { Reply, CheckCheck, MessageSquare } from "lucide-react";
import { PLATFORM_META } from "./platformMeta";

export function CommentsInbox({ comments, onReply }) {
  const [filter, setFilter] = useState("all");
  const [onlyUnreplied, setOnlyUnreplied] = useState(true);
  const [selected, setSelected] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const visible = comments.filter(
    (c) => (filter === "all" || c.platform === filter) && (!onlyUnreplied || !c.replied)
  );

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selectAllVisible = () => setSelected(visible.filter((c) => !c.replied).map((c) => c.id));

  const sendReply = async (ids) => {
    if (!reply.trim() || !ids.length) return;
    setBusy(true);
    const res = await onReply(ids, reply.trim());
    setBusy(false);
    if (res.ok) {
      setMsg({ kind: "ok", text: `Reply sent to ${ids.length} comment${ids.length > 1 ? "s" : ""} (demo).` });
      setReply("");
      setSelected([]);
    } else {
      setMsg({ kind: "error", text: res.error });
    }
  };

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-8" data-testid="social-comments">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-2xl md:text-3xl uppercase text-[#23282B]">Comments Inbox</div>
          <p className="mt-1 text-sm text-[#23282B]/60">Pulled from all linked platforms. Select several and send one answer to all of them.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button onClick={() => setFilter("all")} data-testid="social-comments-filter-all" className={`px-3 py-1.5 text-xs font-bold tracking-[0.12em] uppercase border transition-colors ${filter === "all" ? "bg-[#23282B] text-white border-[#23282B]" : "border-[#23282B]/25 text-[#23282B] hover:border-[#23282B]"}`}>All</button>
        {Object.entries(PLATFORM_META).map(([key, meta]) => (
          <button key={key} onClick={() => setFilter(key)} data-testid={`social-comments-filter-${key}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold tracking-[0.12em] uppercase border transition-colors ${filter === key ? "text-white border-transparent" : "border-[#23282B]/25 text-[#23282B] hover:border-[#23282B]"}`} style={filter === key ? { background: meta.color } : undefined}>
            <i className={meta.icon} /> {meta.name}
          </button>
        ))}
        <label className="ml-auto inline-flex items-center gap-2 text-xs font-semibold text-[#23282B]/70 cursor-pointer">
          <input type="checkbox" data-testid="social-comments-unreplied-toggle" checked={onlyUnreplied} onChange={(e) => setOnlyUnreplied(e.target.checked)} className="accent-[#2F5F80]" />
          Unreplied only
        </label>
      </div>

      {/* Bulk reply bar */}
      <div className="mt-5 bg-[#F1F3F5] border-2 border-[#23282B]/10 p-4" data-testid="social-bulk-reply-bar">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#2F5F80]">
            Reply {selected.length > 0 ? `to ${selected.length} selected` : "— select comments below"}
          </div>
          <button type="button" onClick={selectAllVisible} data-testid="social-select-all" className="text-xs font-semibold text-[#23282B]/70 hover:text-[#2F5F80] underline underline-offset-2">
            Select all unreplied shown
          </button>
        </div>
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <textarea
            data-testid="social-reply-text"
            rows={2}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="One answer, many comments — e.g. Thanks for reaching out! Call (250) 886-1112 or request a free quote at amazingviewcontracting.com."
            className="flex-1 border-2 border-[#23282B]/15 focus:border-[#2F5F80] focus:outline-none px-3 py-2 text-sm resize-y bg-white"
          />
          <button
            onClick={() => sendReply(selected)}
            disabled={busy || !reply.trim() || selected.length === 0}
            data-testid="social-bulk-reply-submit"
            className="inline-flex items-center justify-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-xs font-bold tracking-[0.15em] px-5 py-3 uppercase transition-colors disabled:opacity-40 self-start"
          >
            <CheckCheck size={15} /> {busy ? "Sending…" : `Reply to ${selected.length || "…"}`}
          </button>
        </div>
        {msg && (
          <div data-testid={`social-reply-${msg.kind}`} className={`mt-3 px-3 py-2 text-xs border-l-4 ${msg.kind === "ok" ? "bg-green-50 border-green-600 text-green-800" : "bg-red-50 border-red-500 text-red-700"}`}>
            {msg.text}
          </div>
        )}
      </div>

      {/* Comment list */}
      <div className="mt-5 space-y-3" data-testid="social-comments-list">
        {visible.length === 0 && (
          <div className="border-2 border-dashed border-[#23282B]/20 p-8 text-center text-[#23282B]/60">
            <MessageSquare size={28} className="mx-auto text-[#2F5F80]" />
            <div className="mt-2 text-sm">No comments here — connect a platform or clear the filters.</div>
          </div>
        )}
        {visible.map((c) => {
          const meta = PLATFORM_META[c.platform];
          const checked = selected.includes(c.id);
          return (
            <article key={c.id} data-testid={`social-comment-${c.id}`} className={`border-2 p-4 transition-colors ${checked ? "border-[#2F5F80] bg-[#2F5F80]/5" : "border-[#23282B]/10"}`}>
              <div className="flex items-start gap-3">
                {!c.replied && (
                  <input type="checkbox" data-testid={`social-comment-check-${c.id}`} checked={checked} onChange={() => toggle(c.id)} className="mt-1 accent-[#2F5F80] w-4 h-4 cursor-pointer" />
                )}
                <span className="w-8 h-8 shrink-0 inline-flex items-center justify-center text-white text-sm" style={{ background: meta.color }}>
                  <i className={meta.icon} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold text-[#23282B]">{c.author}</span>
                    <span className="text-[11px] text-[#23282B]/50">{c.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <p className="mt-1 text-sm text-[#23282B]/85 leading-relaxed">{c.text}</p>
                  {c.replied ? (
                    <div className="mt-2 border-l-2 border-green-600 pl-3 text-xs text-[#23282B]/70">
                      <span className="text-green-700 font-bold uppercase tracking-wider text-[10px]">Replied</span> — {c.reply_text}
                    </div>
                  ) : (
                    <button onClick={() => sendReply([c.id])} disabled={!reply.trim() || busy} data-testid={`social-comment-reply-${c.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#2F5F80] hover:text-[#2F5F80] disabled:opacity-40 transition-colors">
                      <Reply size={13} /> Reply with message above
                    </button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
